<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import ConversationApi from 'dashboard/api/inbox/conversation.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');
const { accountId } = useAccount();

const isLoading = ref(true);
const isRefreshing = ref(false);
const columns = ref([]);
const loadError = ref('');

const filterStatus = ref('open');
const filterAssignee = ref('all');
const filterInboxId = ref(0);
const searchQuery = ref('');

const conversationTitle = conversation => {
  const sender = conversation.meta?.sender;
  if (sender?.name) return sender.name;
  return `#${conversation.id}`;
};

const conversationSubtitle = conversation => {
  const parts = [];
  if (conversation.status) parts.push(conversation.status);
  const channel = conversation.meta?.channel;
  if (channel) parts.push(String(channel).replace('Channel::', ''));
  if (conversation.meta?.assignee?.name) {
    parts.push(conversation.meta.assignee.name);
  }
  return parts.join(' · ');
};

const normalizeSearch = s => String(s || '').toLowerCase().trim();

const matchesSearch = conversation => {
  const q = normalizeSearch(searchQuery.value);
  if (!q) return true;
  const title = conversationTitle(conversation).toLowerCase();
  const sub = conversationSubtitle(conversation).toLowerCase();
  const id = String(conversation.id);
  return title.includes(q) || sub.includes(q) || id.includes(q);
};

const statusOptions = computed(() => [
  { value: 'open', label: t('KANBAN.FILTER_STATUS_OPEN') },
  { value: 'pending', label: t('KANBAN.FILTER_STATUS_PENDING') },
  { value: 'resolved', label: t('KANBAN.FILTER_STATUS_RESOLVED') },
  { value: 'all', label: t('KANBAN.FILTER_STATUS_ALL') },
]);

const assigneeOptions = computed(() => [
  { value: 'all', label: t('KANBAN.FILTER_ASSIGNEE_ALL') },
  { value: 'me', label: t('KANBAN.FILTER_ASSIGNEE_ME') },
  { value: 'unassigned', label: t('KANBAN.FILTER_ASSIGNEE_UNASSIGNED') },
]);

const inboxOptions = computed(() => {
  const list = (inboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }));
  return [{ value: 0, label: t('KANBAN.FILTER_INBOX_ALL') }, ...list];
});

const hasLabels = computed(() => (labels.value || []).length > 0);

const visibleColumns = computed(() =>
  columns.value.map(col => ({
    ...col,
    conversations: col.conversations.filter(matchesSearch),
  }))
);

/** Uma listagem + agrupamento no cliente (evita N requests por etiqueta). */
const buildListParamsBulk = (page = 1) => {
  const params = {
    assigneeType: filterAssignee.value,
    page,
    sortBy: 'last_activity_at_desc',
  };
  if (filterStatus.value !== 'all') {
    params.status = filterStatus.value;
  }
  if (filterInboxId.value > 0) {
    params.inboxId = filterInboxId.value;
  }
  return params;
};

const KANBAN_FETCH_TIMEOUT_MS = 45000;

const withTimeout = (promise, ms = KANBAN_FETCH_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), ms)
    ),
  ]);

const fetchConversationsPage = async page => {
  const { data } = await withTimeout(
    ConversationApi.get(buildListParamsBulk(page))
  );
  const inner = data.data ?? data;
  const payload = inner?.payload ?? [];
  const list = Array.isArray(payload) ? payload : [];
  const meta = inner?.meta ?? data.meta ?? {};
  return { list, meta };
};

const MAX_KANBAN_PAGES = 3;

const fetchAllConversationsForBoard = async () => {
  const merged = [];
  let page = 1;
  for (; page <= MAX_KANBAN_PAGES; page += 1) {
    const { list, meta } = await fetchConversationsPage(page);
    merged.push(...list);
    const totalPages = meta?.total_pages ?? meta?.totalPages;
    if (!list.length) break;
    if (totalPages != null && page >= Number(totalPages)) break;
    if (list.length < (meta?.per_page ?? 25)) break;
  }
  return merged;
};

const buildColumnsFromLabels = labelList => {
  columns.value = labelList.map(label => ({
    id: label.id,
    title: label.title,
    color: label.color,
    conversations: [],
  }));
};

const fillColumnsWithConversations = (labelList, allConvs) => {
  columns.value = labelList.map(label => ({
    id: label.id,
    title: label.title,
    color: label.color,
    conversations: allConvs.filter(c =>
      (c.labels || []).includes(label.title)
    ),
  }));
};

const buildColumns = async () => {
  const labelList = labels.value || [];
  if (!labelList.length) {
    columns.value = [];
    return;
  }
  buildColumnsFromLabels(labelList);
  try {
    const allConvs = await fetchAllConversationsForBoard();
    fillColumnsWithConversations(labelList, allConvs);
  } catch (e) {
    loadError.value =
      e?.message === 'timeout'
        ? t(
            'KANBAN.LOAD_TIMEOUT',
            'A listagem de conversas demorou demais. Tente atualizar ou reduzir filtros.'
          )
        : e?.message ||
          t('KANBAN.LOAD_ERROR', 'Não foi possível carregar conversas.');
  }
};

const loadBoard = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  else isRefreshing.value = true;
  loadError.value = '';
  try {
    await withTimeout(
      Promise.all([
        store.dispatch('labels/get'),
        store.dispatch('inboxes/get'),
      ]),
      20000
    );
    await buildColumns();
  } catch (e) {
    loadError.value =
      e?.message || t('KANBAN.LOAD_ERROR', 'Não foi possível carregar o Kanban.');
    columns.value = [];
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
  }
};

const openConversation = conversationId => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: conversationId,
    },
  });
};

const onDragEnd = async (fromColumn, evt) => {
  const { from, to, item } = evt;
  if (!from || !to || from === to) return;

  const fromTitle = fromColumn.title;
  const toColEl = to.closest('[data-kanban-column]');
  const toTitle = toColEl?.getAttribute('data-kanban-column');
  if (!toTitle || fromTitle === toTitle) return;

  const convId = Number(item.getAttribute('data-conv-id'));
  if (!convId) return;

  const conv = fromColumn.conversations.find(c => c.id === convId);
  const currentLabels = [...(conv?.labels || [])];
  const fromIdx = currentLabels.indexOf(fromTitle);
  if (fromIdx >= 0) currentLabels.splice(fromIdx, 1);
  if (!currentLabels.includes(toTitle)) currentLabels.push(toTitle);

  try {
    await store.dispatch('conversationLabels/update', {
      conversationId: convId,
      labels: currentLabels,
    });
    useAlert(t('KANBAN.LABEL_UPDATED'));
    await buildColumns();
  } catch {
    useAlert(t('KANBAN.LABEL_UPDATE_ERROR'));
    await buildColumns();
  }
};

watch([filterStatus, filterAssignee, filterInboxId], () => {
  loadBoard({ silent: true });
});

onMounted(() => {
  loadBoard();
});
</script>

<template>
  <section
    class="flex flex-col w-full min-h-[calc(100vh-3rem)] h-full overflow-hidden bg-n-surface-1"
  >
    <header
      class="flex flex-col gap-3 px-4 py-3 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <h1 class="text-base font-semibold text-n-slate-12 m-0">
          {{ t('KANBAN.TITLE') }}
        </h1>
        <Button
          :label="t('KANBAN.REFRESH')"
          icon="i-lucide-refresh-cw"
          size="sm"
          :is-loading="isRefreshing"
          @click="loadBoard({ silent: true })"
        />
      </div>

      <div
        class="flex flex-wrap items-end gap-2 text-sm"
        role="toolbar"
        :aria-label="t('KANBAN.FILTERS_ARIA')"
      >
        <label class="flex flex-col gap-1 min-w-[8.5rem]">
          <span class="text-xs text-n-slate-11">{{ t('KANBAN.FILTER_STATUS') }}</span>
          <select
            v-model="filterStatus"
            class="h-9 px-2 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option
              v-for="opt in statusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 min-w-[8.5rem]">
          <span class="text-xs text-n-slate-11">{{ t('KANBAN.FILTER_ASSIGNEE') }}</span>
          <select
            v-model="filterAssignee"
            class="h-9 px-2 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option
              v-for="opt in assigneeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 min-w-[10rem] flex-1 max-w-xs">
          <span class="text-xs text-n-slate-11">{{ t('KANBAN.FILTER_INBOX') }}</span>
          <select
            v-model.number="filterInboxId"
            class="h-9 px-2 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 w-full"
          >
            <option
              v-for="opt in inboxOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 min-w-[12rem] flex-1 max-w-sm">
          <span class="text-xs text-n-slate-11">{{ t('KANBAN.FILTER_SEARCH') }}</span>
          <input
            v-model="searchQuery"
            type="search"
            class="h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 w-full"
            :placeholder="t('KANBAN.FILTER_SEARCH_PLACEHOLDER')"
          />
        </label>
      </div>
    </header>

    <div v-if="isLoading" class="flex flex-1 items-center justify-center min-h-[12rem]">
      <Spinner />
      <p class="ml-3 text-sm text-n-slate-11 m-0">
        {{ t('KANBAN.LOADING', 'Carregando etiquetas e conversas…') }}
      </p>
    </div>

    <div
      v-else-if="loadError && !columns.length"
      class="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-sm text-n-ruby-11 min-h-[12rem]"
    >
      <p class="m-0">{{ loadError }}</p>
      <Button :label="t('KANBAN.REFRESH')" size="sm" @click="loadBoard()" />
    </div>

    <div
      v-else-if="!hasLabels"
      class="flex flex-1 items-center justify-center px-6 text-sm text-n-slate-11 min-h-[12rem]"
    >
      {{ t('KANBAN.NO_LABELS') }}
    </div>

    <div
      v-else
      class="flex flex-1 flex-col min-h-0"
    >
      <p
        v-if="loadError"
        class="mx-4 mt-2 mb-0 text-xs text-n-ruby-11"
      >
        {{ loadError }}
      </p>
      <div
        class="flex flex-1 gap-3 p-4 overflow-x-auto overflow-y-hidden min-h-0"
      >
      <div
        v-for="column in visibleColumns"
        :key="column.id || column.title"
        class="flex flex-col flex-shrink-0 w-72 max-h-full bg-n-solid-2 border border-n-weak rounded-xl shadow-sm"
        :data-kanban-column="column.title"
      >
        <div
          class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak text-sm font-medium text-n-slate-12 sticky top-0 bg-n-solid-2 rounded-t-xl"
        >
          <span
            class="size-2.5 rounded-sm shrink-0"
            :style="{ backgroundColor: column.color || '#6b7280' }"
          />
          <span class="truncate flex-1">{{ column.title }}</span>
          <span
            class="text-xs font-normal tabular-nums text-n-slate-11 bg-n-alpha-1 px-1.5 py-0.5 rounded"
          >
            {{ column.conversations.length }}
          </span>
        </div>

        <Draggable
          :list="column.conversations"
          item-key="id"
          group="kanban-conversations"
          class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-[5rem]"
          ghost-class="opacity-50"
          drag-class="shadow-md"
          @end="evt => onDragEnd(column, evt)"
        >
          <template #item="{ element }">
            <div
              :data-conv-id="element.id"
              class="p-2.5 rounded-lg border border-n-weak bg-n-solid-1 cursor-grab active:cursor-grabbing hover:border-n-brand transition-colors"
            >
              <p class="text-sm font-medium text-n-slate-12 m-0 truncate">
                {{ conversationTitle(element) }}
              </p>
              <p class="text-xs text-n-slate-11 m-0 mt-0.5 line-clamp-2">
                {{ conversationSubtitle(element) }}
              </p>
              <button
                type="button"
                class="mt-2 text-xs font-medium text-n-brand hover:underline"
                @click.stop="openConversation(element.id)"
              >
                {{ t('KANBAN.OPEN_CONVERSATION') }}
              </button>
            </div>
          </template>
        </Draggable>
      </div>
      </div>
    </div>
  </section>
</template>